import { Grid, makeStyles } from '@material-ui/core';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getNodeById } from '../../api/node';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import EditorialContentPanel from './EditorialContentPanel';
import GoldenRecordEditorV2 from './GoldenRecordEditorV2';
import { EditorialType, Node, TitleTree } from '../../domain/domain';
import { UiActionType } from '../../reducers/ui.reducer';
import { normalizeTableAlignment, convertTableDivsToP, addColGroupsToTables } from '../../api/common';
import { ApplicationState } from '../../reducers';
import { updateURL, findSubTreeByNodeId } from './utils';

const useStyles = makeStyles(theme => ({
    goldenRecordEditor: {
        paddingRight: 40
    }
}));

type Props = {
    nodeId: number | undefined,
    onRejectNode: (node: Node) => void,
    onApproveNode: (node: Node) => void,
    onApproveEditorialContent: (node: Node, editorialType: EditorialType) => void,
    onRejectEditorialContent: (node: Node, editorialType: EditorialType) => void
}

const EditorPanel : React.FC<Props> = ({nodeId, onRejectNode, onApproveNode, onApproveEditorialContent, onRejectEditorialContent}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const store = useStore<ApplicationState>();
    const editorNode = useSelector((app: ApplicationState) => app.comp.editorNode);
    const treeRoot = useSelector((app: ApplicationState) => app.comp.treeRoot);
    
    const editorNodeRef = useRef<Node | undefined>(editorNode);
    const treeRootRef = useRef<TitleTree | undefined>(treeRoot);
    
    useEffect(() => {
        editorNodeRef.current = editorNode;
    }, [editorNode]);
    
    useEffect(() => {
        treeRootRef.current = treeRoot;
    }, [treeRoot]);

    useEffect(() => {
        if (nodeId === undefined) {
            return;
        }
        getNodeById(nodeId).then(response => {
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
        }).catch(error => {
            console.error(error);
            
            if (error.response?.status === 404) {
                const currentState = store.getState();
                let currentEditorNode = currentState.comp.editorNode;
                const currentTreeRoot = currentState.comp.treeRoot;
                
                if (!currentEditorNode || currentEditorNode.nodeId !== nodeId) {
                    currentEditorNode = editorNodeRef.current;
                }
                
                let parentNodeId: number | undefined;
                
                if (currentEditorNode && currentEditorNode.nodeId === nodeId) {
                    parentNodeId = currentEditorNode.parentId;
                }
                
                if (parentNodeId && currentTreeRoot) {
                    const parentNode = findSubTreeByNodeId(currentTreeRoot, parentNodeId);
                    if (parentNode) {
                        updateURL(currentTreeRoot, parentNode, history);
                        dispatch({
                            type: UiActionType.SNACKBAR_STATE_CHANGED,
                            newState: { 
                                open: true, 
                                message: "The node you were viewing has been deleted. Redirected to parent node.", 
                                severity: "info"
                            }
                        });
                        return;
                    }
                }
                
                if (currentTreeRoot) {
                    history.push(`/component/${currentTreeRoot.citation}`);
                    dispatch({
                        type: UiActionType.SNACKBAR_STATE_CHANGED,
                        newState: { 
                            open: true, 
                            message: "The node you were viewing has been deleted. Redirected to title.", 
                            severity: "info"
                        }
                    });
                    return;
                }
            }
            
            dispatch({
                type: UiActionType.SNACKBAR_STATE_CHANGED,
                newState: { open: true, message: "Node content could not be loaded, please try again later.", severity: "error" }
            });
        })
    }, [nodeId, dispatch, history]);

    if (nodeId === undefined) {
        return <></>
    }

    const parseHtmlForEditor = (html: string): string => {
        if (!html) return '';

        html = normalizeTableAlignment(html, true);
        html = html.replace(/<pl\s*\/>/g, "\u{1F3F7}");
        html = html.replace(/<pl[^>]*>.*?<\/pl>/g, "\u{1F3F7}");
        html = html.replace(/<bl\s*\/>/g, "→");
        html = html.replace(/<bl\s*><\/bl>/g, "→");
        html = html.replace(/<bl_1\s*\/>/g, "↦");
        html = html.replace(/<bl_1\s*><\/bl_1>/g, "↦");
        html = html.replace(/<dl_1\s*\/>/g, "…");
        html = html.replace(/<dl_1\s*><\/dl_1>/g, "…");
        html = html.replace(/<footnote>([\s\S]*?)<\/footnote>/g, "\u2731$1\u2731");

        const replacedHtml = html
            .replace(/<b\b[^>]*>(.*?)<\/b>/gi, '<strong>$1</strong>')
            .replace(/<s\b[^>]*>(.*?)<\/s>/gi, '<del>$1</del>');
        const isWrappedInPTag = /^<p(?:\s+[^>]+)?>[\s\S]*<\/p>$/i.test(replacedHtml);
        const matchResult = replacedHtml.match(/^<p[^>]*>/i);
        const openingPTag = matchResult ? matchResult[0] : '<p>';
        const unwrappedHtml = isWrappedInPTag ? replacedHtml.slice(openingPTag.length, -4) : replacedHtml;

        const parsedHtml = unwrappedHtml.replace(/<t><\/t>/g, '\u200B\u00A0\u00A0\u00A0\u00A0');
        const wrappedHtml = `${openingPTag}${parsedHtml}</p>`;

        return wrappedHtml;
    };


    const replaceStrongWithBold = (content: string): string => {
        let processedContent = convertTableDivsToP(content);
        processedContent = processedContent.replace(/<col\s+group([^>]*)\/?>/gi, '');
        processedContent = processedContent.replace(/<colgroup([^>]*)\/>/gi, '');
        processedContent = addColGroupsToTables(processedContent);
        processedContent = processedContent.replace(/<\/colgroup>\s*<colgroup>/gi, '');
        processedContent = processedContent.replace(/<colgroup>\s*<colgroup>/gi, '<colgroup>');
        processedContent = processedContent
            .replace(/<col(?!group)([^>]*)><\/col>/gi, (match, attrs) => attrs.trim() ? `<col ${attrs.trim()} />` : '<col />')
            .replace(/<col(?!group)([^>]*)>/gi, (match, attrs) => attrs.trim() ? `<col ${attrs.trim()} />` : '<col />')
            .replace(/<hr([^>]*)><\/hr>/gi, (match, attrs) => attrs.trim() ? `<hr ${attrs.trim()} />` : '<hr />')
            .replace(/<hr([^>]*)>/gi, (match, attrs) => attrs.trim() ? `<hr ${attrs.trim()} />` : '<hr />')
            .replace(/<br([^>]*)><\/br>/gi, (match, attrs) => attrs.trim() ? `<br ${attrs.trim()} />` : '<br />')
            .replace(/<br([^>]*)>/gi, (match, attrs) => attrs.trim() ? `<br ${attrs.trim()} />` : '<br />');
        
        return processedContent
            .replace(/<strong\b[^>]*>(.*?)<\/strong>/gi, '<b>$1</b>')
            .replace(/<del\b[^>]*>(.*?)<\/del>/gi, '<s>$1</s>');
    };

    const replaceSpecialCharactersAndWhitespace = (content: string): string => {
        return content
            .replace(/&amp;/g, '&')
            .replace(/&nbsp;&nbsp;&nbsp;&nbsp;/g, '\u2003')
            .replace(/&nbsp;/g, '\u00A0')
            .replace(/\u2003/g, '<t></t>')
            .replace(/\u{1F3F7}/ug, '<pl></pl>')
            .replace(/→/g, '<bl></bl>')
            .replace(/↦/g, '<bl_1></bl_1>')
            .replace(/…/g, '<dl_1></dl_1>')
            .replace(/\u2731([\s\S]*?)\u2731/g, '<footnote>$1</footnote>')
            .replace(/<col\s+group([^>]*)\/?>/gi, '')
            .replace(/<colgroup([^>]*)\/>/gi, '')
            .replace(/<\/colgroup>\s*<colgroup>/gi, '')
            .replace(/<colgroup>\s*<colgroup>/gi, '<colgroup>')
            .replace(/<col(?!group)([^>]*)><\/col>/gi, (match, attrs) => attrs.trim() ? `<col ${attrs.trim()} />` : '<col />')
            .replace(/<col(?!group)([^>]*)>/gi, (match, attrs) => attrs.trim() ? `<col ${attrs.trim()} />` : '<col />')
            .replace(/<hr([^>]*)><\/hr>/gi, (match, attrs) => attrs.trim() ? `<hr ${attrs.trim()} />` : '<hr />')
            .replace(/<hr([^>]*)>/gi, (match, attrs) => attrs.trim() ? `<hr ${attrs.trim()} />` : '<hr />')
            .replace(/<br([^>]*)><\/br>/gi, (match, attrs) => attrs.trim() ? `<br ${attrs.trim()} />` : '<br />')
            .replace(/<br([^>]*)>/gi, (match, attrs) => attrs.trim() ? `<br ${attrs.trim()} />` : '<br />');
    };

    return (
        <Grid container direction='column' className="flex-gap-1" wrap='nowrap'>
            <Grid item style={{flexGrow: 1}}>
                <GoldenRecordEditorV2
                    className={classes.goldenRecordEditor}
                    onRejectNode={onRejectNode}
                    onApproveNode={onApproveNode}
                    parseHtmlForEditor={parseHtmlForEditor}
                    replaceStrongWithBold={replaceStrongWithBold}
                    sanitizeContent={replaceSpecialCharactersAndWhitespace}
                />
            </Grid>
            <Grid item>
                <EditorialContentPanel
                    onApproveEditorialContent={onApproveEditorialContent}
                    onRejectEditorialContent={onRejectEditorialContent}
                    parseHtmlForEditor={parseHtmlForEditor}
                    replaceStrongWithBold={replaceStrongWithBold}
                    sanitizeContent={replaceSpecialCharactersAndWhitespace}
                />
            </Grid>
        </Grid>
    )
}

export default EditorPanel;
